import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Btn, Input } from "@/components/erp/ui";
import { err } from "@/components/erp/flow/FlowCommon";
import { Plus, Trash2 } from "lucide-react";

const Tol = ({ d, load }) => {
  const [v, setV] = useState({ tol_default: d.settings.tol_default, tol_min: d.settings.tol_min, tol_max: d.settings.tol_max });
  if (!d.is_owner) return null;
  const save = async () => { try { await api.put("/flow/settings", { tol_default: Number(v.tol_default), tol_min: Number(v.tol_min), tol_max: Number(v.tol_max) }); toast.success("Toleransi disimpan"); load(); } catch (e) { err(e); } };
  return (
    <div className="flex flex-wrap items-end gap-2 text-xs" data-testid="qc-tol-settings">
      {[["tol_min", "Min (mm)"], ["tol_default", "Default (mm)"], ["tol_max", "Maks (mm)"]].map(([k, l]) => (
        <label key={k} className="flex flex-col gap-1">{l}<Input type="number" step="0.05" className="!w-24" data-testid={`qc-set-${k}`} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} /></label>
      ))}
      <Btn variant="ghost" data-testid="qc-tol-save" onClick={save}>Simpan toleransi</Btn>
    </div>
  );
};

const PresetRow = ({ p, tolDef, reload, isNew }) => {
  const [v, setV] = useState({ product: p?.product || "", tolerance: p?.tolerance ?? tolDef, measurements: p?.measurements?.length ? p.measurements : [{ name: "", target: "" }] });
  const setM = (i, k, x) => setV({ ...v, measurements: v.measurements.map((m, j) => (j === i ? { ...m, [k]: x } : m)) });
  const tid = isNew ? "new" : p.product_lc.replace(/\s+/g, "-");
  const save = async () => { try { await api.put("/flow/qc-presets", { ...v, tolerance: Number(v.tolerance) }); toast.success("Acuan produk disimpan"); reload(); if (isNew) setV({ product: "", tolerance: tolDef, measurements: [{ name: "", target: "" }] }); } catch (e) { err(e); } };
  const del = async () => { try { await api.delete(`/flow/qc-presets/${encodeURIComponent(p.product)}`); reload(); } catch (e) { err(e); } };
  return (
    <div className="border-b border-slate-100 py-3" data-testid={`qc-preset-${tid}`}>
      <div className="flex flex-wrap gap-2 items-center">
        <Input className="!w-56" placeholder="Nama produk (sama dengan di WO)" data-testid={`qc-preset-product-${tid}`} value={v.product} disabled={!isNew} onChange={(e) => setV({ ...v, product: e.target.value })} />
        <span className="text-xs">±</span><Input type="number" step="0.05" className="!w-20" data-testid={`qc-preset-tol-${tid}`} value={v.tolerance} onChange={(e) => setV({ ...v, tolerance: e.target.value })} /><span className="text-xs">mm</span>
      </div>
      {v.measurements.map((m, i) => (
        <div key={i} className="flex gap-2 items-center mt-1">
          <Input className="!w-48" placeholder="titik ukur (mis. tinggi total)" data-testid={`qc-preset-name-${tid}-${i}`} value={m.name} onChange={(e) => setM(i, "name", e.target.value)} />
          <Input type="number" className="!w-28" placeholder="target mm" data-testid={`qc-preset-target-${tid}-${i}`} value={m.target} onChange={(e) => setM(i, "target", e.target.value)} />
          <button type="button" aria-label="hapus titik" onClick={() => setV({ ...v, measurements: v.measurements.filter((_, j) => j !== i) })}><Trash2 className="w-4 h-4 text-slate-400" /></button>
        </div>
      ))}
      <div className="flex gap-2 mt-2">
        <button type="button" className="text-xs text-sky-700 inline-flex items-center gap-1" data-testid={`qc-preset-add-${tid}`} onClick={() => setV({ ...v, measurements: [...v.measurements, { name: "", target: "" }] })}><Plus className="w-3 h-3" /> titik ukur</button>
        <Btn variant="ghost" data-testid={`qc-preset-save-${tid}`} onClick={save}>{isNew ? "Tambah acuan" : "Simpan"}</Btn>
        {!isNew && <Btn variant="ghost" data-testid={`qc-preset-del-${tid}`} onClick={del}>Hapus</Btn>}
      </div>
    </div>
  );
};

const Items = ({ d, load }) => {
  const [rows, setRows] = useState(d.items);
  if (!d.is_owner) return null;
  const save = async () => { try { const { data } = await api.put("/flow/settings", { qc_items: rows.filter((r) => r.label.trim()) }); if (data?.qc_items) setRows(data.qc_items); toast.success("Poin cek QC disimpan"); load(); } catch (e) { err(e); } };
  return (
    <div className="mt-4 border-t border-slate-100 pt-3" data-testid="qc-items-settings">
      <div className="text-sm font-semibold mb-1">Poin cek QC (admin utama)</div>
      <p className="text-[11px] text-slate-500 mb-2">"Ukuran" wajib untuk sparepart. Bila "Tidak ada part patah" dijawab Tidak, QC wajib isi part & jumlah lalu WO otomatis kembali ke produksi untuk cetak ulang.</p>
      {rows.map((r, i) => (
        <div key={r.key || i} className="flex gap-2 items-center mb-1">
          <Input className="!w-72" data-testid={`qc-item-label-${i}`} value={r.label} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
          <button type="button" aria-label="hapus poin" data-testid={`qc-item-del-${i}`} onClick={() => setRows(rows.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-slate-400" /></button>
        </div>
      ))}
      <div className="flex gap-2 mt-2">
        <button type="button" className="text-xs text-sky-700 inline-flex items-center gap-1" data-testid="qc-item-add" onClick={() => setRows([...rows, { key: "", label: "" }])}><Plus className="w-3 h-3" /> poin cek</button>
        <Btn variant="ghost" data-testid="qc-items-save" onClick={save}>Simpan poin cek</Btn>
      </div>
    </div>
  );
};

export const QcSettings = ({ d, load }) => {
  const [rows, setRows] = useState([]);
  const reload = () => api.get("/flow/qc-presets").then(({ data }) => setRows(data)).catch(err);
  useEffect(() => { reload(); }, []);
  return (
    <details className="rounded-2xl border border-slate-200 bg-white p-4 mt-6" data-testid="qc-settings">
      <summary className="font-semibold cursor-pointer">Pengaturan QC: poin cek, toleransi & ukuran acuan ({rows.length})</summary>
      <p className="text-xs text-slate-500 mt-2 mb-2">Acuan otomatis terisi di form QC bila nama produk WO sama. Bisa juga tersimpan langsung dari form QC.</p>
      <Tol d={d} load={load} />
      {rows.map((p) => <PresetRow key={`${p.product_lc}-${p.updated_at}`} p={p} tolDef={d.settings.tol_default} reload={() => { reload(); load(); }} />)}
      <PresetRow isNew tolDef={d.settings.tol_default} reload={() => { reload(); load(); }} />
      <Items d={d} load={load} />
    </details>
  );
};
